import React from 'react';
import { Modal, Gauge } from '@/shared/ui';
import type { AdsAudit, AdsAuditPlatformBreakdown } from '../api/campaignsApi';
import styles from './AdsAuditPlatformBreakdownModal.module.css';

interface Props {
  audit: AdsAudit;
  isOpen: boolean;
  onClose: () => void;
}

const PLATFORM_LABEL: Record<'google' | 'meta', string> = {
  google: 'Google Ads',
  meta: 'Meta Ads',
};

function fmtSpend(spend: number): string {
  if (spend <= 0) return '$0';
  if (spend >= 1000) return `$${(spend / 1000).toFixed(1)}k`;
  return `$${Math.round(spend)}`;
}

function weightPct(platform: AdsAuditPlatformBreakdown, total: number): number {
  if (total <= 0) return 0;
  return Math.round((platform.spend / total) * 100);
}

export function AdsAuditPlatformBreakdownModal({ audit, isOpen, onClose }: Props) {
  const platforms = audit.breakdown?.platforms ?? {};
  const entries = (
    Object.entries(platforms) as Array<['google' | 'meta', AdsAuditPlatformBreakdown | undefined]>
  ).filter((e): e is ['google' | 'meta', AdsAuditPlatformBreakdown] => !!e[1]);

  const totalSpend = entries.reduce((s, [, p]) => s + p.spend, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ads Score breakdown" size="lg">
      <div className={styles.container}>
        <div className={styles.summary}>
          Combined score is the spend-weighted average of each platform's sub-score.
        </div>

        <div className={styles.platforms}>
          {entries.length === 0 && (
            <div className={styles.emptyHint}>
              Per-platform breakdown isn't available for this audit.
            </div>
          )}
          {entries.map(([key, platform]) => {
            const weight = weightPct(platform, totalSpend);
            const topDeductions = [...platform.breakdown.deductions]
              .sort((a, b) => b.points - a.points)
              .slice(0, 5);
            return (
              <div key={key} className={styles.platformCard}>
                <div className={styles.platformHeader}>
                  <Gauge score={platform.score} size={80} showGrade="none" />
                  <div className={styles.platformMeta}>
                    <div className={styles.platformName}>{PLATFORM_LABEL[key]}</div>
                    <div className={styles.platformStats}>
                      30d spend {fmtSpend(platform.spend)}
                      {entries.length > 1 && ` · weight ${weight}%`}
                    </div>
                  </div>
                </div>
                <div className={styles.deductionsLabel}>Top issues</div>
                {topDeductions.length === 0 ? (
                  <div className={styles.cleanNote}>No deductions — clean run.</div>
                ) : (
                  <ul className={styles.deductionsList}>
                    {topDeductions.map((d) => (
                      <li key={`${key}-${d.rule}`} className={styles.deductionItem}>
                        <div className={styles.deductionHeader}>
                          <span className={styles.deductionRule}>{d.rule}</span>
                          <span className={styles.deductionPoints}>-{d.points}</span>
                        </div>
                        <div className={styles.deductionDetail}>{d.detail}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
